import ExportPane from "@storyboards/modules/storyboardSharing/components/exportPane.es6";
import classNames from "classnames";
import filter from "lodash/filter";
import find from "lodash/find";
import PropTypes from "prop-types";
import React from "react";
import StoryboardAssignmentService from "../services/storyboardAssignmentService";
import StoryboardLinkService from "../services/storyboardLinkService";

class StoryboardShareContainer extends React.Component {
  constructor(props) {
    super(props);

    this.handleModelChange = this.handleModelChange.bind(this);
    this.handleUpdateAssignments = this.handleUpdateAssignments.bind(this);
    this.updateAssignment = this.updateAssignment.bind(this);
    this.removeAssignment = this.removeAssignment.bind(this);
    this.generateLink = this.generateLink.bind(this);
    this.regenerateLink = this.regenerateLink.bind(this);
    this.deleteLink = this.deleteLink.bind(this);
    this.handleClose = this.handleClose.bind(this);
    this.addAssignmentsToStore = this.addAssignmentsToStore.bind(this);
    this.switchPane = this.switchPane.bind(this);

    this.initializeState(props);
    this.initializeReduxStore(props);
    this.initializeAssignments();
  }

  componentDidMount() {
    ACLUISetup.aclPanel();
    this.storyboardShareStore.subscribe(this.handleModelChange);
    this.handleModelChange();
  }

  initializeReduxStore(props) {
    let reducers = Redux.combineReducers({
      assignments: AssignmentReducer.users,
      link: LinkReducer.link,
    });
    this.storyboardShareStore = Redux.createStore(
      reducers,
      {
        assignments: this.getFormattedAdminUsers(),
        link: props.storyboardShareLink || props.storyboardLink,
      },
      window.devToolsExtension ? window.devToolsExtension() : f => f
    );
  }

  initializeState(props) {
    this.state = {
      showPanel: props.showPanel,
      paneMode: "users",
      userPaneMode: "currentUsers",
      assignments: this.getFormattedAdminUsers(),
      link: props.storyboardShareLink || props.storyboardLink,
      storyboard_link: props.storyboardShareLink,
    };
  }

  initializeAssignments() {
    if (this.props.storyboardId) {
      StoryboardAssignmentService.getAssignments(this.props.storyboardId, this.props.users, this.props.groups)
        .then(({ userAssignments, groupAssignments }) => this.addAssignmentsToStore(userAssignments, groupAssignments))
        .catch(() => {
          /* deliberately ignore errors - happens when user has no access to permissions */
        });
    }
  }

  getFormattedAdminUsers() {
    let isCreatorAdmin = false;
    let creator = this.props.creator;
    let admins = this.props.admins.map(admin => {
      if (!isCreatorAdmin) isCreatorAdmin = creator && creator.id === admin.id;
      return {
        userId: admin.id,
        name: admin.name,
        email: admin.email,
        role: creator && creator.id === admin.id ? "creator" : "admin",
      };
    });
    if (creator && !isCreatorAdmin) {
      admins.push({
        userId: creator.id,
        name: creator.name,
        email: creator.email,
        role: "creator",
      });
    }
    return admins;
  }

  findAssignmentByUserId(userId) {
    let model = this.storyboardShareStore.getState();
    let userAssignments = filter(model.assignments, function(assignment) {
      return !assignment.isAdmin;
    });
    return find(userAssignments, function(a) {
      return a.userId === userId;
    });
  }

  findAssignmentByGroupId(groupId) {
    let model = this.storyboardShareStore.getState();
    let userAssignments = filter(model.assignments, function(assignment) {
      return !assignment.isAdmin;
    });
    return find(userAssignments, function(a) {
      return a.groupId === groupId;
    });
  }

  handleModelChange() {
    let model = this.storyboardShareStore.getState();
    if (model.link.access_type) {
      this.setState({
        assignments: model.assignments,
        storyboard_link: model.link,
        link: model.link.uid,
      });
    } else {
      this.setState({
        assignments: model.assignments,
        link: model.link,
        storyboard_link: model.link,
      });
    }
  }

  handleUpdateAssignments(users, groups) {
    this.addAssignments(users.added, groups.added);
    this.removeUserAssignemnts(users.removed);
    this.removeGroupAssignemnts(groups.removed);
  }

  handleClose() {
    this.props.onClose();
    this.setState({
      paneMode: "users",
      userPaneMode: "currentUsers",
    });
  }

  switchPane(pane) {
    this.setState({
      paneMode: pane,
      userPaneMode: "currentUsers",
    });
  }

  addAssignments(usersAdded, groupsAdded) {
    StoryboardAssignmentService.addAssignments(
      this.props.storyboardId,
      this.props.users,
      this.props.groups,
      usersAdded,
      groupsAdded
    )
      .then(({ userAssignments, groupAssignments }) => this.addAssignmentsToStore(userAssignments, groupAssignments))
      .catch(() => {
        /* deliberately ignore errors - happens when user has no access to permissions */
      });
  }

  removeUserAssignemnts(usersRemoved) {
    usersRemoved.forEach(user => {
      let assignment = this.findAssignmentByUserId(user.id);
      if (assignment) {
        this.removeAssignment(assignment.assignmentId);
      }
    });
  }

  removeGroupAssignemnts(groupsRemoved) {
    groupsRemoved.forEach(group => {
      let assignment = this.findAssignmentByGroupId(group.id);
      if (assignment) {
        this.removeAssignment(assignment.assignmentId);
      }
    });
  }

  removeAssignment(assignmentId) {
    StoryboardAssignmentService.removeAssignment(this.props.storyboardId, assignmentId)
      .then(assignmentId => {
        this.removeAssignmentFromStore(assignmentId);
      })
      .catch(() => {
        /* deliberately ignore errors - happens when user has no access to permissions */
      });
  }

  updateAssignment(assignmentId, role) {
    StoryboardAssignmentService.updateAssignment(this.props.storyboardId, assignmentId, role)
      .then(assignment => {
        this.updateAssignmentInStore(assignment.id, assignment.role);
      })
      .catch(() => {
        /* deliberately ignore errors - happens when user has no access to permissions */
      });
  }

  generateLink(accessType, launchpadGroups, workflowGroups) {
    const link = this.state.storyboard_link ? this.state.storyboard_link.uid : this.state.link;
    StoryboardLinkService.generateLink(this.props.storyboardId, accessType, launchpadGroups, workflowGroups, link).then(
      storyboardLink => {
        this.storyboardShareStore.dispatch(LinkActions.setLink(storyboardLink));
      }
    );
  }

  regenerateLink(accessType, launchpadGroups, workflowGroups) {
    const link = this.state.storyboard_link ? this.state.storyboard_link.uid : this.state.link;
    StoryboardLinkService.regenerateLink(
      this.props.storyboardId,
      link,
      accessType,
      launchpadGroups,
      workflowGroups
    ).then(storyboardLink => {
      this.storyboardShareStore.dispatch(LinkActions.setLink(storyboardLink));
    });
  }

  deleteLink() {
    StoryboardLinkService.deleteLink(this.props.storyboardId, this.state.link).then(() => {
      this.storyboardShareStore.dispatch(LinkActions.removeLink());
    });
  }

  addAssignmentsToStore(userAssignments, groupAssignments) {
    userAssignments.forEach(assignment => {
      this.addUserToStore(
        assignment.assignmentId,
        assignment.userId,
        assignment.name,
        assignment.email,
        assignment.role
      );
    });
    groupAssignments.forEach(assignment => {
      this.addGroupToStore(assignment.assignmentId, assignment.groupId, assignment.name, assignment.role);
    });
  }

  addUserToStore(assignmentId, userId, userName, email, role) {
    this.storyboardShareStore.dispatch(
      AssignmentActions.addUserAssignment({
        assignmentId: assignmentId,
        userId: userId,
        name: userName,
        email: email,
        role: role,
      })
    );
  }

  addGroupToStore(assignmentId, groupId, groupName, role) {
    this.storyboardShareStore.dispatch(
      AssignmentActions.addGroupAssignment({
        assignmentId: assignmentId,
        groupId: groupId,
        name: groupName,
        role: role,
      })
    );
  }

  removeAssignmentFromStore(assignmentId) {
    this.storyboardShareStore.dispatch(AssignmentActions.removeAssignment(assignmentId));
  }

  updateAssignmentInStore(assignmentId, role) {
    this.storyboardShareStore.dispatch(AssignmentActions.updateAssignmentRole(assignmentId, role));
  }

  getPanelBody() {
    let panelBody;
    if (this.state.paneMode === "users") {
      panelBody = (
        <UserPane
          assignments={this.state.assignments}
          admins={this.getFormattedAdminUsers()}
          users={this.props.users}
          groups={this.props.groups}
          paneMode={this.state.userPaneMode}
          onPermissionChange={this.updateAssignment}
          onRemoveUser={this.removeAssignment}
          onUpdateAssignment={this.handleUpdateAssignments}
        />
      );
    } else if (this.state.paneMode === "link") {
      const { storyboard_link } = this.state;
      const publicLink = storyboard_link
        ? StoryboardLinkService.storyboardFullPublicUrl(storyboard_link.uid)
        : StoryboardLinkService.storyboardFullPublicUrl(this.state.link);
      panelBody = (
        <LinkPane
          publicLink={publicLink}
          onGenerate={this.generateLink}
          onRegenerate={this.regenerateLink}
          onDelete={this.deleteLink}
          groups={this.props.groups}
          launchpadGroups={this.props.launchpadGroups}
          selectedGroups={storyboard_link ? storyboard_link.group_ids : []}
          selectedLaunchpadGroups={storyboard_link ? storyboard_link.launchpad_group_ids : []}
          linkType={storyboard_link.access_type || "anyone"}
        />
      );
    } else {
      panelBody = <ExportPane onExportStoryboardToPDFClick={this.showExportToPDFOptions} />;
    }
    return <div className="panel__content">{panelBody}</div>;
  }

  showExportToPDFOptions = () => {
    this.handleClose();
    this.props.onExportStoryboardToPDFClick();
  };

  render() {
    const aclPanelClasses = classNames("share-panel acl-panel acl-panel--slide-right storyboard-share-panel", {
      "is-open": this.props.isOpen,
    });
    return (
      <div>
        <div className={aclPanelClasses} data-has-overlay="true" data-clickable-overlay="true">
          <button className="share-panel__close" onClick={this.handleClose}>
            <i className="acl-i-times close__icon" />
          </button>

          <div className="panel__body">
            <StoryboardShareHeader />
            <StoryboardShareNav
              exportStoryboardFlipper={this.props.exportStoryboardFlipper}
              paneMode={this.state.paneMode}
              onNavSelected={this.switchPane}
              showLinkNav={this.props.showLinkNav}
              showMemberNav={this.props.showMemberNav}
            />
            {this.getPanelBody()}
          </div>
        </div>
        <div className="acl-panel-overlay" onClick={this.handleClose} />
      </div>
    );
  }
}

StoryboardShareContainer.propTypes = {
  storyboardId: PropTypes.number,
  storyboardLink: PropTypes.string,
  linkType: PropTypes.string,
  admins: PropTypes.array,
  users: PropTypes.array,
  groups: PropTypes.array,
  launchpadGroups: PropTypes.array,
  isOpen: PropTypes.bool,
  onClose: PropTypes.func,
  exportStoryboardFlipper: PropTypes.bool,
  showLinkNav: PropTypes.bool,
  showMemberNav: PropTypes.bool,
  onExportStoryboardToPDFClick: PropTypes.func,
};

window.StoryboardShareContainer = StoryboardShareContainer;
